import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { chatSidebarWidthAtom, layoutModeAtom } from '../../../../atoms/v3/uiAtoms';

interface ChatSidebarResizerProps {
  className?: string;
}

/**
 * Chat 侧边栏拖拽调整组件
 * 
 * 功能：
 * - 提供垂直拖拽条用于调整 chat 侧边栏宽度
 * - 使用 localStorage 持久化宽度设置
 * - 仅在桌面模式下启用
 * - 限制最小和最大宽度
 */
export const ChatSidebarResizer: React.FC<ChatSidebarResizerProps> = ({ 
  className = '' 
}) => {
  const [chatSidebarWidth, setChatSidebarWidth] = useAtom(chatSidebarWidthAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const resizerRef = useRef<HTMLDivElement>(null);

  // 宽度限制（rem 单位）
  const MIN_WIDTH = 20; // 最小 20rem
  const MAX_WIDTH = 40; // 最大 40rem

  // 处理拖拽开始
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // 仅在桌面模式下启用拖拽
    if (layoutMode !== 'desktop') return;

    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX);
    setStartWidth(chatSidebarWidth);

    // 捕获指针以确保拖拽过程中不会丢失事件
    if (resizerRef.current) {
      resizerRef.current.setPointerCapture(e.pointerId);
    }
  }, [layoutMode, chatSidebarWidth]);

  // 处理拖拽移动
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    e.preventDefault();

    // 计算拖拽距离（向左拖拽为正值，向右拖拽为负值）
    const deltaX = startX - e.clientX;
    
    // 将像素转换为 rem（假设 1rem = 14px 在桌面端）
    const deltaRem = deltaX / 14;
    
    // 计算新宽度
    const newWidth = startWidth + deltaRem;
    
    // 限制在最小和最大宽度之间
    const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
    
    // 更新宽度
    setChatSidebarWidth(clampedWidth);
  }, [isDragging, startX, startWidth, setChatSidebarWidth]);

  // 处理拖拽结束
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    e.preventDefault();
    setIsDragging(false);

    // 释放指针捕获
    if (resizerRef.current) {
      resizerRef.current.releasePointerCapture(e.pointerId);
    }
  }, [isDragging]);

  // 添加全局事件监听器以处理拖拽过程中鼠标移出组件的情况
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalPointerMove = (e: PointerEvent) => {
      const deltaX = startX - e.clientX;
      const deltaRem = deltaX / 14;
      const newWidth = startWidth + deltaRem;
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setChatSidebarWidth(clampedWidth);
    };

    const handleGlobalPointerUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('pointermove', handleGlobalPointerMove);
    document.addEventListener('pointerup', handleGlobalPointerUp);

    return () => {
      document.removeEventListener('pointermove', handleGlobalPointerMove);
      document.removeEventListener('pointerup', handleGlobalPointerUp);
    };
  }, [isDragging, startX, startWidth, setChatSidebarWidth]);

  // 如果不是桌面模式，不渲染拖拽条
  if (layoutMode !== 'desktop') {
    return null;
  }

  return (
    <div
      ref={resizerRef}
      className={`
        absolute left-0 top-0 bottom-0 w-1 
        cursor-col-resize 
        hover:bg-ios-blue/20 dark:hover:bg-ios-dark-blue/30
        active:bg-ios-blue/40 dark:active:bg-ios-dark-blue/50
        transition-colors duration-150
        z-30
        ${isDragging ? 'bg-ios-blue/40 dark:bg-ios-dark-blue/50' : 'bg-transparent'}
        ${className}
      `}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{
        // 确保拖拽条在拖拽时有足够的点击区域
        marginLeft: '-2px',
        width: '4px',
        touchAction: 'none', // 防止触摸设备上的默认行为
      }}
      aria-label="调整对话栏宽度"
    />
  );
};
